import { useState } from "react";
import { Button, Modal } from "@shop-ledger/ui-components";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body: string;
  confirmLabel?: string;
  danger?: boolean;
  pending?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

/**
 * Shared confirm-dialog UX for §9.6's mocked mutations (suspend/reactivate/backup
 * trigger). The *mutation itself* is mocked (`mockPlatformApiClient.ts`), but the
 * confirm-before-you-act interaction pattern is real and reusable as-is once a real
 * `PlatformApiClient` implementation exists — nothing here needs to change when that
 * happens, only which client is wired up in `main.tsx`.
 */
export function ConfirmDialog({ open, title, body, confirmLabel = "Confirm", danger, pending, onConfirm, onCancel }: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      footer={
        <>
          <Button type="button" variant="ghost" size="md" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={danger ? "danger" : "primary"}
            size="md"
            onClick={() => void onConfirm()}
            disabled={pending}
          >
            {pending ? "Working…" : confirmLabel}
          </Button>
        </>
      }
    >
      <p>{body}</p>
    </Modal>
  );
}

/** Small hook so every page needing a confirm-dialog doesn't hand-roll the same
 * open/pending/error state three times (suspend, reactivate, backup). */
export function useConfirmDialog<TResult>() {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(action: () => Promise<TResult>, onSuccess?: (result: TResult) => void) {
    setPending(true);
    setError(null);
    try {
      const result = await action();
      onSuccess?.(result);
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Action failed");
    } finally {
      setPending(false);
    }
  }

  return { open, setOpen, pending, error, setError, run };
}
